import analyticsApi from '../api/analytics';

// Проста функція для генерації UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

class AnalyticsTracker {
  constructor() {
    this.sessionId = null;
    this.sessionStartTime = null;
    this.currentPageStartTime = null;
    this.enabled = true; // Увімкнено - формат даних налаштовано
  }

  // Ініціалізація трекера
  init() {
    // Отримуємо або створюємо sessionId
    this.sessionId = sessionStorage.getItem('analytics_session_id');

    if (!this.sessionId) {
      this.sessionId = generateUUID();
      sessionStorage.setItem('analytics_session_id', this.sessionId);
      this.sessionStartTime = Date.now();
      this.createSession();
    } else {
      this.sessionStartTime = parseInt(sessionStorage.getItem('analytics_session_start') || Date.now());
    }

    // Відстежуємо закриття сторінки
    window.addEventListener('beforeunload', () => this.handleBeforeUnload());

    // Відстежуємо видимість сторінки
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());

    return this;
  }

  // Створення нової сесії
  async createSession() {
    if (!this.enabled) return;

    try {
      const sessionData = {
        sessionId: this.sessionId,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent,
        deviceType: this.getDeviceType(),
        browser: this.getBrowser(),
        operatingSystem: this.getOS(),
        country: null, // Буде визначено на backend
        city: null,
        startedAt: new Date().toISOString(),
        isActive: true,
        pagesViewed: 0
      };

      sessionStorage.setItem('analytics_session_start', Date.now().toString());

      await analyticsApi.trackSession(sessionData);
    } catch (error) {
      console.error('Помилка при створенні сесії:', error);
    }
  }

  // Відстеження відвідування сторінки
  async trackPageView(pageUrl, pageTitle) {
    if (!this.enabled) return;

    try {
      this.currentPageStartTime = Date.now();

      const visitData = {
        sessionId: this.sessionId,
        pageUrl: pageUrl || window.location.pathname,
        pageTitle: pageTitle || document.title,
        referrer: document.referrer || null,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent,
        deviceType: this.getDeviceType(),
        browser: this.getBrowser(),
        operatingSystem: this.getOS(),
        visitedAt: new Date().toISOString()
      };

      await analyticsApi.trackPageVisit(visitData);

      // Оновлюємо сесію
      await analyticsApi.trackSession({
        sessionId: this.sessionId,
        endedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Помилка при відстеженні сторінки:', error);
    }
  }

  // Обробка закриття сторінки
  handleBeforeUnload() {
    if (!this.enabled) return;

    // Відправляємо час, проведений на сторінці
    if (this.currentPageStartTime) {
      const timeSpent = Math.floor((Date.now() - this.currentPageStartTime) / 1000);

      // Використовуємо sendBeacon для надійної відправки при закритті
      const data = JSON.stringify({
        sessionId: this.sessionId,
        timeSpent,
        endedAt: new Date().toISOString()
      });

      navigator.sendBeacon('/api/stats/track/session/end', data);
    }

    // Завершуємо сесію
    analyticsApi.endSession(this.sessionId).catch(() => {});
  }

  // Обробка зміни видимості сторінки
  handleVisibilityChange() {
    if (document.hidden) {
      // Сторінка приховується
      if (this.currentPageStartTime) {
        const timeSpent = Math.floor((Date.now() - this.currentPageStartTime) / 1000);

        analyticsApi.trackSession({
          sessionId: this.sessionId,
          timeSpent,
          endedAt: new Date().toISOString()
        }).catch(() => {});
      }
    } else {
      // Сторінка знову видима
      this.currentPageStartTime = Date.now();
    }
  }

  // Визначення типу пристрою
  getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  // Визначення браузера
  getBrowser() {
    const ua = navigator.userAgent;
    let browser = 'Unknown';

    if (ua.indexOf('Firefox') > -1) {
      browser = 'Firefox';
    } else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) {
      browser = 'Opera';
    } else if (ua.indexOf('Trident') > -1) {
      browser = 'Internet Explorer';
    } else if (ua.indexOf('Edge') > -1 || ua.indexOf('Edg') > -1) {
      browser = 'Edge';
    } else if (ua.indexOf('Chrome') > -1) {
      browser = 'Chrome';
    } else if (ua.indexOf('Safari') > -1) {
      browser = 'Safari';
    }

    return browser;
  }

  // Визначення ОС
  getOS() {
    const ua = navigator.userAgent;
    let os = 'Unknown';

    if (ua.indexOf('Win') > -1) os = 'Windows';
    else if (ua.indexOf('Mac') > -1) os = 'MacOS';
    else if (ua.indexOf('Linux') > -1) os = 'Linux';
    else if (ua.indexOf('Android') > -1) os = 'Android';
    else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) os = 'iOS';

    return os;
  }

  // Отримання IP адреси (спроба)
  async getClientIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return null;
    }
  }

  // Вимкнути трекінг
  disable() {
    this.enabled = false;
  }

  // Увімкнути трекінг
  enable() {
    this.enabled = true;
  }

  // Прив'язка сесії до користувача після авторизації
  async linkToUser(userId) {
    if (!this.enabled || !this.sessionId || !userId) return;

    try {
      await analyticsApi.linkSessionToUser(this.sessionId, userId);
    } catch (error) {
      console.error('Помилка при прив\'язці сесії до користувача:', error);
    }
  }
}

// Створюємо єдиний екземпляр трекера
const tracker = new AnalyticsTracker();

export default tracker;
